import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Layers, Plus, Edit2, Check, X, ChevronUp, ChevronDown,
  ToggleLeft, ToggleRight, Search, AlertCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const GROUP_COLORS = {
  'Core Food Supply': '#16a34a',
  'Beverage Supply': '#2563eb',
  'Perishables / Specialty': '#9333ea',
  'Packaging / Consumables': '#d97706',
  'Operations / Maintenance': '#6366f1',
  'Services / Utility-linked Vendors': '#0891b2',
  'General / Fallback': '#6b7280',
};

const VendorCategoryMaster = () => {
  const { theme } = useTheme();
  const currentColors = colors[theme];

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({ category_name: '', category_group: 'General / Fallback' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getVendorCategories();
      setCategories(res.data.categories || []);
    } catch (e) {
      console.error('Error loading categories:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleCreate = async () => {
    if (!createForm.category_name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.createVendorCategory(createForm);
      setCreateForm({ category_name: '', category_group: 'General / Fallback' });
      setShowCreate(false);
      loadCategories();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (categoryId) => {
    setSaving(true);
    setError('');
    try {
      await api.updateVendorCategory(categoryId, editForm);
      setEditingId(null);
      loadCategories();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await api.toggleVendorCategoryActive(cat.category_id);
      loadCategories();
    } catch (e) {
      console.error('Error toggling category:', e);
    }
  };

  const handleSortChange = async (cat, direction) => {
    const newOrder = cat.sort_order + direction;
    if (newOrder < 1) return;
    try {
      // Swap with adjacent
      const other = categories.find((c) => c.sort_order === newOrder);
      if (other) {
        await api.updateVendorCategory(other.category_id, { sort_order: cat.sort_order });
      }
      await api.updateVendorCategory(cat.category_id, { sort_order: newOrder });
      loadCategories();
    } catch (e) {
      console.error('Error updating sort order:', e);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.category_id);
    setEditForm({
      category_name: cat.category_name,
      category_code: cat.category_code,
      category_group: cat.category_group,
    });
    setError('');
  };

  const filtered = categories.filter(
    (c) =>
      c.category_name.toLowerCase().includes(search.toLowerCase()) ||
      c.category_code.toLowerCase().includes(search.toLowerCase()) ||
      (c.category_group || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = categories.filter((c) => c.is_active).length;
  const groupKeys = Object.keys(GROUP_COLORS);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="vendor-category-master-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1" style={{ color: currentColors.textPrimary }}>
              Vendor Categories
            </h1>
            <p className="text-sm" style={{ color: currentColors.textSecondary }}>
              Canonical master data — {activeCount} active, {categories.length - activeCount} inactive
            </p>
          </div>
          <button
            onClick={() => { setShowCreate(true); setError(''); }}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-indigo-600 hover:bg-indigo-700 transition-colors"
            data-testid="create-category-btn"
          >
            <Plus size={16} /> New Category
          </button>
        </div>
      </motion.div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: currentColors.textSecondary }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search categories..."
          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl"
          style={{
            background: currentColors.cardBg,
            color: currentColors.textPrimary,
            border: `1px solid ${currentColors.border}`,
            boxShadow: getNeumorphicShadow(theme),
          }}
          data-testid="category-search-input"
        />
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 px-4 py-3 rounded-xl flex items-center gap-2 text-sm"
            style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
            data-testid="category-error"
          >
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 rounded-xl p-5 overflow-hidden"
            style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme), border: `1px solid ${currentColors.border}` }}
            data-testid="create-category-form"
          >
            <h3 className="font-semibold text-sm mb-3" style={{ color: currentColors.textPrimary }}>
              New Category
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: currentColors.textSecondary }}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={createForm.category_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, category_name: e.target.value }))}
                  placeholder="e.g. Spices & Seasoning"
                  className="w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: currentColors.background, color: currentColors.textPrimary, border: `1px solid ${currentColors.border}` }}
                  data-testid="create-category-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: currentColors.textSecondary }}>
                  Group
                </label>
                <select
                  value={createForm.category_group}
                  onChange={(e) => setCreateForm((f) => ({ ...f, category_group: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: currentColors.background, color: currentColors.textPrimary, border: `1px solid ${currentColors.border}` }}
                  data-testid="create-category-group-select"
                >
                  {groupKeys.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={handleCreate}
                  disabled={saving || !createForm.category_name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="create-category-submit-btn"
                >
                  <Check size={14} /> {saving ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm rounded-lg"
                  style={{ background: currentColors.background, color: currentColors.textSecondary }}
                  data-testid="create-category-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-40" data-testid="categories-loading">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="categories-empty">
            <Layers className="mx-auto mb-3" size={36} style={{ color: currentColors.textSecondary }} />
            <p className="text-sm" style={{ color: currentColors.textSecondary }}>
              {search ? 'No categories match your search' : 'No categories found'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left" data-testid="categories-table">
            <thead>
              <tr style={{ background: currentColors.background, borderBottom: `1px solid ${currentColors.border}` }}>
                <th className="px-4 py-3 text-xs font-semibold w-16" style={{ color: currentColors.textSecondary }}>#</th>
                <th className="px-4 py-3 text-xs font-semibold" style={{ color: currentColors.textSecondary }}>Category</th>
                <th className="px-4 py-3 text-xs font-semibold" style={{ color: currentColors.textSecondary }}>Code</th>
                <th className="px-4 py-3 text-xs font-semibold" style={{ color: currentColors.textSecondary }}>Group</th>
                <th className="px-4 py-3 text-xs font-semibold text-center w-24" style={{ color: currentColors.textSecondary }}>Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-right w-40" style={{ color: currentColors.textSecondary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cat, i) => {
                const isEditing = editingId === cat.category_id;
                const groupColor = GROUP_COLORS[cat.category_group] || '#6b7280';

                return (
                  <motion.tr
                    key={cat.category_id}
                    className={`border-b transition-colors ${!cat.is_active ? 'opacity-50' : ''}`}
                    style={{ borderColor: currentColors.border }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: cat.is_active ? 1 : 0.5 }}
                    data-testid={`category-row-${cat.category_code}`}
                  >
                    {/* Sort Order */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-mono" style={{ color: currentColors.textSecondary }}>
                          {cat.sort_order}
                        </span>
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleSortChange(cat, -1)}
                            className="p-0.5 hover:opacity-70"
                            style={{ color: currentColors.textSecondary }}
                            data-testid={`sort-up-${cat.category_code}`}
                          >
                            <ChevronUp size={12} />
                          </button>
                          <button
                            onClick={() => handleSortChange(cat, 1)}
                            className="p-0.5 hover:opacity-70"
                            style={{ color: currentColors.textSecondary }}
                            data-testid={`sort-down-${cat.category_code}`}
                          >
                            <ChevronDown size={12} />
                          </button>
                        </div>
                      </div>
                    </td>

                    {/* Category Name */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.category_name}
                          onChange={(e) => setEditForm((f) => ({ ...f, category_name: e.target.value }))}
                          className="w-full px-2 py-1 text-sm rounded"
                          style={{ background: currentColors.background, color: currentColors.textPrimary, border: `1px solid ${currentColors.border}` }}
                          data-testid="edit-category-name-input"
                        />
                      ) : (
                        <span className="text-sm font-medium" style={{ color: currentColors.textPrimary }}>
                          {cat.category_name}
                        </span>
                      )}
                    </td>

                    {/* Code */}
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-mono px-2 py-0.5 rounded"
                        style={{ background: currentColors.background, color: currentColors.textSecondary }}
                      >
                        {cat.category_code}
                      </span>
                    </td>

                    {/* Group */}
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <select
                          value={editForm.category_group}
                          onChange={(e) => setEditForm((f) => ({ ...f, category_group: e.target.value }))}
                          className="w-full px-2 py-1 text-sm rounded"
                          style={{ background: currentColors.background, color: currentColors.textPrimary, border: `1px solid ${currentColors.border}` }}
                          data-testid="edit-category-group-select"
                        >
                          {groupKeys.map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ background: groupColor + '18', color: groupColor }}
                        >
                          {cat.category_group}
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(cat)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          background: cat.is_active ? '#dcfce7' : '#fef2f2',
                          color: cat.is_active ? '#166534' : '#991b1b',
                        }}
                        data-testid={`toggle-active-${cat.category_code}`}
                      >
                        {cat.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleUpdate(cat.category_id)}
                            disabled={saving}
                            className="p-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                            data-testid="edit-save-btn"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setError(''); }}
                            className="p-1.5 rounded-lg"
                            style={{ background: currentColors.background, color: currentColors.textSecondary }}
                            data-testid="edit-cancel-btn"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(cat)}
                          className="p-1.5 rounded-lg hover:opacity-70"
                          style={{ color: currentColors.accentBlue }}
                          data-testid={`edit-btn-${cat.category_code}`}
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 text-xs" style={{ color: currentColors.textSecondary }}>
        Vendor categories are managed exclusively from Control Room. Downstream systems sync via{' '}
        <code className="px-1 py-0.5 rounded" style={{ background: currentColors.background }}>
          GET /api/master-data/vendor-categories?active_only=true
        </code>
      </div>
    </div>
  );
};

export default VendorCategoryMaster;
